import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PageHeader from '../../Shared/PageHeader'
import DataTable from '../../Shared/DataTable'
import ExportMenu from '../../Shared/ExportMenu'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getLogs } from '../../../services/configServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { formatDate } from '../../../utils/format'
import useDebounce from '../../../utils/useDebounce'
import useUserNames, { primeUserNames } from '../../../utils/useUserNames'

const emptyFilters = { user: '', from: '', to: '', operation: '' }

const dash = value => (value === null || value === undefined || value === '' ? '—' : value)

/**
 * O log guarda `userId` (GUID) e `userEmail`; o nome é do SSO.
 *
 * Ordem: nome resolvido, e-mail, ID. O e-mail vem antes do ID porque já
 * identifica a pessoa — só o GUID é que não diz nada a ninguém.
 */
const getUser = (nomeDe, row) => {
  if (row.userId) {
    const nome = nomeDe(row.userId)
    // `nomeDe` devolve o próprio ID quando o SSO não conhece mais o usuário.
    if (nome && nome !== row.userId) return nome
  }
  return row.userEmail || row.userId || ''
}
const getDate = row => row.createdAt || row.date || ''
const getEquipment = row => row.equipment || ''
const getIp = row => row.ip || ''
const getOperation = row => row.operation || ''
const getPrevious = row => row.previousData
const getNext = row => row.newData

/** Hora isolada da data do log; "—" quando a data não é válida. */
const formatTime = value => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleTimeString('pt-BR')
}

const toText = value => {
  if (value === null || value === undefined || value === '') return ''
  return typeof value === 'string' ? value : JSON.stringify(value)
}

const truncate = (text, size = 60) => (text.length > size ? `${text.slice(0, size)}...` : text)

const Audit = () => {
  const { notify, FeedbackSnackbar } = useFeedback()

  const [filters, setFilters] = useState(emptyFilters)
  const [applied, setApplied] = useState(emptyFilters)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [detail, setDetail] = useState(null)

  const { nomeDe } = useUserNames(
    useMemo(() => rows.map(row => row.userId).filter(Boolean), [rows])
  )

  useDebounce(
    () => {
      setApplied(filters)
      setPage(0)
    },
    500,
    [filters]
  )

  // A API filtra por período, usuário e operação; a tela não refiltra no cliente.
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getLogs({
        from: applied.from ? `${applied.from}T00:00:00` : '',
        to: applied.to ? `${applied.to}T23:59:59` : '',
        user: applied.user.trim(),
        operation: applied.operation.trim(),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      })
      setRows(response.data.result || [])
      setTotal(response.data.total || 0)
    } catch (error) {
      notify(mensagemDeErro(error, 'Não foi possível carregar os registros de auditoria.'), 'error')
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [applied, page, rowsPerPage, notify])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Export leva todo o filtro, não só a página: pagina a API em lotes e aquece
  // os nomes dos usuários (a coluna Usuário resolve o userId via SSO).
  const fetchAllForExport = useCallback(async () => {
    const BLOCO = 200
    const todas = []
    let offset = 0
    for (;;) {
      const response = await getLogs({
        from: applied.from ? `${applied.from}T00:00:00` : '',
        to: applied.to ? `${applied.to}T23:59:59` : '',
        user: applied.user.trim(),
        operation: applied.operation.trim(),
        limit: BLOCO,
        offset,
      })
      const itens = response.data.result || []
      todas.push(...itens)
      offset += BLOCO
      if (offset >= (response.data.total || 0) || itens.length === 0) break
    }
    await primeUserNames(todas.map(row => row.userId))
    return todas
  }, [applied])

  const updateFilter = (field, value) => setFilters(current => ({ ...current, [field]: value }))

  const jsonColumn = (id, label, getValue) => ({
    id,
    label,
    element: row => {
      const value = getValue(row)
      const text = toText(value)
      if (!text) return '—'
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={text}>
            <Box component="span">{truncate(text)}</Box>
          </Tooltip>
          <IconButton size="small" onClick={() => setDetail({ title: label, value })}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    },
  })

  const columns = [
    { id: 'user', label: 'Usuário', element: row => dash(getUser(nomeDe, row)) },
    { id: 'date', label: 'Data', element: row => formatDate(getDate(row)) },
    { id: 'time', label: 'Hora', element: row => formatTime(getDate(row)) },
    { id: 'equipment', label: 'Equipamento', element: row => dash(getEquipment(row)) },
    { id: 'ip', label: 'IP', element: row => dash(getIp(row)) },
    { id: 'operation', label: 'Operação realizada', element: row => dash(getOperation(row)) },
    jsonColumn('previous', 'Dados anteriores', getPrevious),
    jsonColumn('next', 'Dados novos', getNext),
  ]

  const exportColumns = [
    { header: 'Usuário', accessor: row => dash(getUser(nomeDe, row)) },
    { header: 'Data', accessor: row => formatDate(getDate(row)) },
    { header: 'Hora', accessor: row => formatTime(getDate(row)) },
    { header: 'Equipamento', accessor: row => dash(getEquipment(row)) },
    { header: 'IP', accessor: row => dash(getIp(row)) },
    { header: 'Operação realizada', accessor: row => dash(getOperation(row)) },
    { header: 'Dados anteriores', accessor: row => toText(getPrevious(row)) || '—' },
    { header: 'Dados novos', accessor: row => toText(getNext(row)) || '—' },
  ]

  return (
    <Box>
      <PageHeader
        title="Auditoria"
        subtitle="Registro das operações realizadas no sistema"
        actions={
          <ExportMenu
            rows={rows}
            resolveRows={fetchAllForExport}
            columns={exportColumns}
            fileName="auditoria"
            title="Auditoria"
            disabled={total === 0}
          />
        }
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        A trilha cobre as operações de escrita feitas por esta API. O histórico anterior a ela não
        existe no banco legado, então registros antigos podem vir sem dados anteriores/novos.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Usuário"
              value={filters.user}
              onChange={event => updateFilter('user', event.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={filters.from}
              onChange={event => updateFilter('from', event.target.value)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={filters.to}
              onChange={event => updateFilter('to', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Operação"
              value={filters.operation}
              onChange={event => updateFilter('operation', event.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyMessage="Nenhum registro de auditoria encontrado."
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setRowsPerPage(parseInt(event.target.value, 10))
          setPage(0)
        }}
      />

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle>{detail && detail.title}</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              maxHeight: '60vh',
              overflow: 'auto',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {detail && JSON.stringify(detail.value, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setDetail(null)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar />
    </Box>
  )
}

export default Audit
